import React, {
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
} from "react";

import {
  useSpring,
  a,
  SpringConfig,
  SpringValue,
  SpringValues,
  to,
} from "@react-spring/web";
import { useAnimateInSpring } from "hooks/useAnimateInSpring";
import { defaultSpringConfig } from "components/Animations/SpringProperties/SpringProperties";

import { LazyImage } from "components/Image/Index";

import classNames from "classnames";
import * as fromProjects from "store/Projects";

interface IOwnSpecificProps {
  animateIn: boolean;
  item: fromProjects.IContentImageSliderDefaultItem;
  velocity: SpringValues;
  isBeingDragged: boolean;
}

type IOwnProps = IOwnSpecificProps;

type IProps = IOwnProps;

const ImageComponent: React.FC<IProps> = ({
  animateIn,
  item,
  velocity,
  isBeingDragged,
  ...props
}) => {
  const [draggingScaleFactor] = useState(0.06);
  const animateInSpring = useAnimateInSpring(animateIn);

  const zoomSpringConfig: SpringConfig = {
    ...defaultSpringConfig,
    tension: 170,
  };

  const [zoomSpring, setZoomSpring] = useSpring(() => ({
    s: 0,
    immediate: false,
    config: zoomSpringConfig,
  }));

  useEffect(() => {
    setZoomSpring({ s: isBeingDragged ? 1 : 0 });
  }, [isBeingDragged]);

  const tileStyles = {
    transform: animateInSpring.active.to(
      (x) => `translateX(calc((${x} * -50px) + 50px))`
    ),
  };

  const tileContentStyles = {
    backgroundColor: item.backgroundColor != null ? item.backgroundColor : "",
    opacity: animateInSpring.active.to((x) => x),
    transform: to(
      [zoomSpring.s, velocity.x],
      (s: number, x) =>
        `scale(calc(1 + (${s * draggingScaleFactor}))) translateX(${x}px)`
    ),
  };

  const getImage = () => {
    if (item.url != null) {
      return (
        <LazyImage
          src={"/assets/images/work/" + item.url}
          alt={item.alt}
          width="100%"
          heightRatio={item.heightRatio}
          animateImageIn={true}
          animationType={"fadeIn"}
        />
      );
    }
    return null;
  };

  return (
    <a.div
      className={classNames(
        "tile-wrapper",
        { ["width-" + item.tileWidth]: item.tileWidth != null },
        { isBeingDragged }
      )}
      style={tileStyles}
    >
      <a.div className="tile-content" style={tileContentStyles}>
        {getImage()}
        {item.content != null ? item.content : null}
      </a.div>
    </a.div>
  );
};

export const Image = ImageComponent;
